import { useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/src/modules/auth/useAuth';
import { useUsersList } from '../hooks';
import { useShallow } from 'zustand/react/shallow';
import { selectUserQuery, useUserFilters } from '../store/filters.store';
import { PlanBadge, RoleBadge, UserStatusBadge } from './UserStatusBadge';
import { UserActionsDropdown } from './UserActionsDropdown';
import { TableSkeleton } from './LoadingSkeleton';
import { EmptyState, ErrorState } from './EmptyState';
import type { AdminUserRow } from '../types';

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function UserTable() {
  const filters = useUserFilters();
  const navigate = useNavigate();
  const { user } = useAuth();
  // SUPER_ADMIN check relies on backend AdminAccessGuard already authorising
  // the session; we mirror UI affordances based on Firebase custom claims if
  // exposed, otherwise default to ADMIN-tier capabilities.
  // The simplest safe default: assume ADMIN unless the backend says otherwise.
  const canDelete = !!user; // backend enforces SUPER_ADMIN; UI shows option, server rejects if not allowed.
  const canChangeRole = canDelete;

  const query = useUserFilters(useShallow(selectUserQuery));
  const { data, isLoading, isError, error, refetch } = useUsersList(query);

  const columns = useMemo<ColumnDef<AdminUserRow>[]>(
    () => [
      {
        id: 'select',
        header: () => {
          const allSelected = !!data && data.items.length > 0 && data.items.every((r) => filters.selectedIds.includes(r.id));
          return (
            <input
              type="checkbox"
              aria-label="Select all"
              checked={allSelected}
              onChange={(e) => {
                if (!data) return;
                const ids = data.items.map((r) => r.id);
                if (e.target.checked) filters.setSelected(Array.from(new Set([...filters.selectedIds, ...ids])));
                else filters.setSelected(filters.selectedIds.filter((id) => !ids.includes(id)));
              }}
            />
          );
        },
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label={`Select ${row.original.email}`}
            checked={filters.selectedIds.includes(row.original.id)}
            onChange={() => filters.toggleSelected(row.original.id)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        accessorKey: 'name',
        header: 'User',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.avatar ? (
              <img src={row.original.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-electric-violet/30 flex items-center justify-center text-xs font-bold">
                {(row.original.name?.[0] ?? row.original.email[0]).toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-medium text-sm leading-tight">{row.original.name}</div>
              <div className="text-[10px] text-on-surface-variant">{row.original.email}</div>
            </div>
          </div>
        ),
      },
      { accessorKey: 'role', header: 'Role', cell: ({ row }) => <RoleBadge role={row.original.role} /> },
      { accessorKey: 'status', header: 'Status', cell: ({ row }) => <UserStatusBadge status={row.original.status} /> },
      { accessorKey: 'plan', header: 'Plan', cell: ({ row }) => <PlanBadge plan={row.original.plan} /> },
      {
        accessorKey: 'invitationsCount',
        header: 'Invites',
        cell: ({ row }) => <span className="text-sm">{row.original.invitationsCount}</span>,
      },
      {
        accessorKey: 'revenue',
        header: 'Revenue',
        cell: ({ row }) => (
          <span className="text-sm">₹{(row.original.revenue / 100).toFixed(2)}</span>
        ),
      },
      { accessorKey: 'createdAt', header: 'Joined', cell: ({ row }) => <span className="text-xs text-on-surface-variant">{fmtDate(row.original.createdAt)}</span> },
      { accessorKey: 'lastActiveAt', header: 'Last active', cell: ({ row }) => <span className="text-xs text-on-surface-variant">{fmtDate(row.original.lastActiveAt)}</span> },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <UserActionsDropdown
            user={row.original}
            canDelete={canDelete}
            canChangeRole={canChangeRole}
          />
        ),
      },
    ],
    [data, filters, canDelete, canChangeRole],
  );

  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <TableSkeleton />;
  if (isError) return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  if (!data || data.items.length === 0) {
    return <EmptyState title="No users match these filters" hint="Try clearing filters or widening the search." />;
  }

  return (
    <div className="glass-panel rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-surface-container-low">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-on-surface-variant"
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => navigate(`/users/${row.original.id}`)}
                className="border-t border-border-subtle hover:bg-white/5 cursor-pointer"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination total={data.meta.total} page={data.meta.page} totalPages={data.meta.totalPages} />
    </div>
  );
}

function Pagination({ page, totalPages, total }: { page: number; totalPages: number; total: number }) {
  const filters = useUserFilters();
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border-subtle text-xs text-on-surface-variant">
      <span>
        Page {page} of {totalPages || 1} ({total} total)
      </span>
      <div className="flex gap-2">
        <button
          disabled={page <= 1}
          onClick={() => filters.setFilter('page', page - 1)}
          className="px-3 py-1.5 rounded-md border border-border-subtle disabled:opacity-40 hover:bg-white/5"
        >
          Prev
        </button>
        <button
          disabled={page >= (totalPages || 1)}
          onClick={() => filters.setFilter('page', page + 1)}
          className="px-3 py-1.5 rounded-md border border-border-subtle disabled:opacity-40 hover:bg-white/5"
        >
          Next
        </button>
      </div>
    </div>
  );
}
